import {
  DARK_BROWN,
  LIGHT_GREY,
  VARIABLE_TAG_BACKGROUND,
} from "styles/oneOffColor";

import { builderLabelSx } from "../../styles";

// Brand palette mirrors the MUI theme (purple.*, primary.main, green.main).
const PURPLE_50 = "#f2f3fe";
const PURPLE_500 = "#B1B3F8";
const PURPLE_700 = "#8084FF";
const PRIMARY = "#014638";
const PRIMARY_700 = "#165C4E";
const BORDER = "#D9D5CD";
const WARNING = "#b9a311";

const styles = {
  creditChipRow: { display: "flex", justifyContent: "flex-end", mb: 1.5 },
  creditChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 0.75,
    whiteSpace: "nowrap",
    backgroundColor: "#FBF6E6",
    border: "1px solid #E7D9A6",
    color: DARK_BROWN,
    fontWeight: 600,
    fontSize: 13,
    px: 1.5,
    py: 0.75,
    borderRadius: "999px",
    "& i": { color: "#C99A2E", fontSize: 14 },
  },
  callout: {
    backgroundColor: "#EEF4FF",
    borderRadius: "14px",
    p: "16px 20px",
    mb: 3,
  },
  calloutTitle: {
    display: "flex",
    alignItems: "center",
    gap: 1.25,
    fontWeight: 600,
    fontSize: 16,
    color: "#2D5BD0",
  },
  calloutBody: {
    color: "#3A66D4",
    fontSize: 13.5,
    lineHeight: 1.5,
    mt: 0.75,
  },
  modeGrid: {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
    gap: 2,
    mb: 3,
  },
  mode: (selected: boolean, locked: boolean) => ({
    position: "relative",
    border: `1.5px solid ${selected ? PRIMARY : BORDER}`,
    borderRadius: "16px",
    p: "20px 20px 18px",
    cursor: locked ? "default" : "pointer",
    backgroundColor: locked ? "#F9F8F7" : "#fff",
    borderStyle: locked ? "dashed" : "solid",
    boxShadow: selected ? `0 0 0 3px rgba(1,70,56,0.08)` : "none",
    transition: "border-color .15s, box-shadow .15s",
    "&:hover": {
      borderColor: locked ? BORDER : selected ? PRIMARY : "#afbfb1",
    },
  }),
  modeIcon: (ai: boolean) => ({
    width: 40,
    height: 40,
    borderRadius: "11px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 20,
    mb: 1.5,
    color: ai ? "#fff" : "#776B5D",
    background: ai
      ? `linear-gradient(135deg, ${PRIMARY}, ${PRIMARY_700})`
      : "#f7f6f0",
  }),
  modeHeading: {
    fontWeight: 600,
    fontSize: 16,
    color: "#151e16",
    display: "flex",
    alignItems: "center",
    gap: 1,
  },
  modeBody: {
    color: "#776B5D",
    fontSize: 13,
    mt: 0.5,
    lineHeight: 1.45,
  },
  modeCheck: (selected: boolean) => ({
    position: "absolute",
    top: 16,
    right: 16,
    width: 22,
    height: 22,
    borderRadius: "50%",
    border: `2px solid ${selected ? PRIMARY : BORDER}`,
    backgroundColor: selected ? PRIMARY : "transparent",
    color: "#fff",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
  }),
  modeUpgradeBtn: { mt: 2, width: "100%" },
  badgeRec: {
    fontWeight: 600,
    fontSize: 10,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    backgroundColor: PURPLE_50,
    color: PURPLE_700,
    px: 1,
    py: "3px",
    borderRadius: "999px",
  },
  badgePro: {
    fontWeight: 600,
    fontSize: 10,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    backgroundColor: VARIABLE_TAG_BACKGROUND,
    color: DARK_BROWN,
    px: 1,
    py: "3px",
    borderRadius: "999px",
  },
  section: { mb: 3 },
  fieldLabel: { ...builderLabelSx, mb: 1, display: "block" },
  fieldHint: { fontSize: 12, color: "#9e9e9e", mt: 0.75 },
  chipRow: { display: "flex", flexWrap: "wrap", gap: 1 },
  chip: (on: boolean) => ({
    fontSize: 13,
    fontWeight: 500,
    px: 1.75,
    py: 0.875,
    borderRadius: "999px",
    border: `1px solid ${on ? PRIMARY : BORDER}`,
    backgroundColor: on ? PRIMARY : "#fff",
    color: on ? "#fff" : "#151e16",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 0.75,
    textTransform: "none",
    "&:hover": { borderColor: PRIMARY, backgroundColor: on ? PRIMARY : "#fff" },
  }),
  aiSources: {
    display: "flex",
    alignItems: "flex-start",
    gap: 1.5,
    background:
      "linear-gradient(180deg, rgba(177,179,248,0.10), rgba(177,179,248,0.05))",
    border: `1px solid ${PURPLE_50}`,
    borderRadius: "14px",
    p: "14px 18px",
  },
  aiSourcesIcon: {
    width: 34,
    height: 34,
    borderRadius: "9px",
    flexShrink: 0,
    background: `linear-gradient(135deg, ${PRIMARY}, ${PRIMARY_700})`,
    color: "#fff",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 16,
  },
  creditCost: {
    display: "flex",
    alignItems: "center",
    gap: 1.25,
    border: `1px solid ${BORDER}`,
    borderRadius: "12px",
    p: "12px 16px",
    mt: 2,
  },
  creditCostIcon: {
    width: 30,
    height: 30,
    borderRadius: "8px",
    flexShrink: 0,
    backgroundColor: "#DBEFDD",
    color: PRIMARY_700,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 15,
  },
  creditCostWarn: {
    display: "flex",
    alignItems: "center",
    gap: 1.25,
    border: "1px solid rgba(192,57,43,0.35)",
    backgroundColor: "rgba(192,57,43,0.06)",
    borderRadius: "12px",
    p: "12px 16px",
    mt: 2,
  },
  creditCostWarnIcon: {
    width: 30,
    height: 30,
    borderRadius: "8px",
    flexShrink: 0,
    backgroundColor: "rgba(192,57,43,0.12)",
    color: "#C0392B",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 15,
  },
  creditCostWarnTitle: { fontWeight: 700, fontSize: 13, color: "#C0392B" },
  creditBanner: {
    display: "flex",
    alignItems: "center",
    gap: 1.75,
    backgroundColor: "#f7f6f0",
    border: `1px solid ${WARNING}`,
    borderRadius: "14px",
    p: "14px 18px",
    mb: 3,
  },
  bannerIcon: (tone: "ai" | "warn") => ({
    width: 40,
    height: 40,
    borderRadius: "11px",
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 19,
    color: "#fff",
    background:
      tone === "warn"
        ? WARNING
        : `linear-gradient(135deg, ${PRIMARY}, ${PRIMARY_700})`,
  }),
  resumeLoader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    py: 10,
  },
  genWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    py: 8,
    px: 4,
  },
  genSpark: {
    width: 64,
    height: 64,
    borderRadius: "18px",
    background: `linear-gradient(135deg, ${PRIMARY}, ${PRIMARY_700})`,
    color: "#fff",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 30,
    mb: 3,
    boxShadow: "0 12px 30px rgba(1,70,56,0.28)",
    "@keyframes cwpulse": {
      "0%, 100%": { transform: "scale(1)" },
      "50%": { transform: "scale(1.06)" },
    },
    animation: "cwpulse 1.6s ease-in-out infinite",
  },
  genBar: {
    width: 420,
    maxWidth: "80%",
    height: 8,
    borderRadius: "999px",
    backgroundColor: LIGHT_GREY,
    overflow: "hidden",
    mt: 3,
  },
  genBarFill: (pct: number) => ({
    height: "100%",
    width: `${pct}%`,
    backgroundColor: PRIMARY,
    borderRadius: "999px",
    transition: "width .4s ease",
  }),
  genList: {
    mt: 4,
    width: 420,
    maxWidth: "80%",
    display: "flex",
    flexDirection: "column",
    gap: 1.25,
    textAlign: "left",
  },
  genItem: { display: "flex", alignItems: "center", gap: 1.5 },
  genItemDot: (status: string) => ({
    width: 26,
    height: 26,
    borderRadius: "50%",
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    backgroundColor:
      status === "done"
        ? "#E3F0E9"
        : status === "generating"
          ? "transparent"
          : LIGHT_GREY,
    color: status === "done" ? PRIMARY : "#9e9e9e",
  }),
  genItemText: (status: string) => ({
    fontSize: 14,
    color: status === "pending" || status === "failed" ? "#b3ada3" : "#151e16",
    "& strong": { fontWeight: 700 },
  }),
  revGrid: {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", md: "320px 1fr" },
    border: `1px solid ${PURPLE_50}`,
    borderRadius: "16px",
    overflow: "hidden",
    minHeight: 520,
  },
  revList: {
    borderRight: { md: `1px solid ${PURPLE_50}` },
    backgroundColor: "#F9F8F7",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  },
  revListHead: {
    p: "14px 16px",
    borderBottom: `1px solid ${PURPLE_50}`,
    fontWeight: 600,
    fontSize: 13,
    color: "#776B5D",
  },
  headFailed: { color: "#C0392B", fontWeight: 600 },
  revScroll: { flex: 1, overflowY: "auto", p: 1, maxHeight: 520 },
  revItem: (selected: boolean) => ({
    display: "grid",
    gridTemplateColumns: "34px 1fr auto",
    alignItems: "center",
    gap: 1.25,
    p: 1.25,
    borderRadius: "10px",
    cursor: "pointer",
    backgroundColor: selected ? "#fff" : "transparent",
    boxShadow: selected ? `inset 0 0 0 1.5px ${PRIMARY}` : "none",
    "&:hover": { backgroundColor: "#fff" },
  }),
  bubble: {
    width: 34,
    height: 34,
    borderRadius: "50%",
    backgroundColor: "#DBEFDD",
    color: PRIMARY_700,
    fontSize: 13,
    fontWeight: 600,
  },
  revName: { fontWeight: 600, fontSize: 13.5, color: "#151e16" },
  revSub: {
    fontSize: 11.5,
    color: "#9e9e9e",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  statusIcon: (color: string) => ({ color, fontSize: 16 }),
  revDetail: {
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#fff",
  },
  revDetailHead: {
    p: "16px 24px",
    borderBottom: `1px solid ${PURPLE_50}`,
    display: "flex",
    alignItems: "center",
    gap: 1.5,
  },
  revDetailBody: { p: "22px 24px", flex: 1, overflowY: "auto" },
  persBar: {
    display: "flex",
    alignItems: "flex-start",
    gap: 1.25,
    background:
      "linear-gradient(180deg, rgba(177,179,248,0.12), rgba(177,179,248,0.04))",
    border: `1px solid ${PURPLE_50}`,
    borderRadius: "12px",
    p: "12px 14px",
    mb: 2.25,
  },
  email: {
    border: `1px solid ${PURPLE_50}`,
    borderRadius: "14px",
    p: "18px 20px",
    position: "relative",
  },
  emailRow: {
    display: "flex",
    gap: 1.5,
    pb: 1.5,
    mb: 1.75,
    borderBottom: `1px solid ${PURPLE_50}`,
    alignItems: "baseline",
  },
  emailLabel: { fontSize: 12, color: "#9e9e9e", width: 58, flexShrink: 0 },
  emailVal: { flex: 1, fontSize: 14, color: "#151e16" },
  emailBody: {
    fontSize: 14,
    lineHeight: 1.7,
    color: "#151e16",
    // Empty <p></p> spacers + min-height (not margins) so edit/view render alike.
    "& p": { m: 0, minHeight: "1.2em" },
  },
  emailBodyEditor: {
    "& .ProseMirror": { minHeight: 220, fontSize: 14, lineHeight: 1.7 },
  },
  mark: {
    backgroundColor: "rgba(177,179,248,0.30)",
    borderRadius: "4px",
    px: "3px",
  },
  caveat: {
    display: "flex",
    alignItems: "flex-start",
    gap: 1,
    mt: 1.75,
    fontSize: 12,
    color: "#9e9e9e",
    lineHeight: 1.5,
  },
  detailFoot: {
    borderTop: `1px solid ${PURPLE_50}`,
    p: "14px 24px",
    display: "flex",
    alignItems: "center",
    gap: 1.25,
  },
  approvedFlag: {
    display: "inline-flex",
    alignItems: "center",
    gap: 0.75,
    fontWeight: 600,
    fontSize: 12,
    color: PRIMARY_700,
  },
  pill: (tone: "draft" | "scheduled") => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 0.5,
    fontSize: 11.5,
    fontWeight: 700,
    px: 1,
    py: 0.5,
    borderRadius: "999px",
    backgroundColor: tone === "draft" ? "#f7f6f0" : PURPLE_50,
    color: tone === "draft" ? WARNING : PURPLE_700,
  }),
  regenError: {
    display: "flex",
    alignItems: "center",
    gap: 1.5,
    backgroundColor: "rgba(192,57,43,0.06)",
    border: "1px solid rgba(192,57,43,0.30)",
    borderRadius: "12px",
    p: "12px 14px",
    mb: 2,
    fontSize: 13,
    color: "#151e16",
  },
  regenOverlay: {
    position: "absolute",
    inset: 0,
    zIndex: 2,
    backgroundColor: "rgba(255,255,255,0.82)",
    borderRadius: "14px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 1.5,
    fontSize: 14,
    fontWeight: 500,
    color: PRIMARY_700,
  },
  mutedNote: { color: "#9e9e9e", fontWeight: 400 },
  infoTitle: { fontWeight: 600, fontSize: 13, color: "#151e16" },
  infoBody: { fontSize: 12.5, color: "#776B5D", mt: 0.25, lineHeight: 1.5 },
  costSub: { fontSize: 12, color: "#776B5D" },
  bannerTitle: { fontWeight: 600, fontSize: 14 },
  bannerBody: { fontSize: 12.5, color: "#776B5D", mt: 0.25 },
  genHeading: { fontWeight: 700, fontSize: 22, color: "#151e16" },
  genSub: { color: "#776B5D", fontSize: 14, mt: 0.75 },
  genCount: { mt: 1.5, fontSize: 13, color: "#776B5D" },
  footInfo: { fontSize: 13, color: "#776B5D" },
  footActions: { display: "flex", alignItems: "center", gap: 2 },
  retryFailedButton: {
    textTransform: "none",
    fontSize: 13,
    color: PURPLE_700,
    flexShrink: 0,
  },
  confirmContent: { textAlign: "center", pt: 4, px: 3 },
  confirmIcon: {
    width: 34,
    height: 34,
    borderRadius: "50%",
    backgroundColor: "rgba(185,163,17,0.15)",
    color: WARNING,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 15,
    mb: 1.5,
  },
  confirmTitle: {
    fontWeight: 700,
    fontSize: 18,
    color: "#151e16",
    mb: 1,
  },
  confirmText: { fontSize: 14, color: "#776B5D", lineHeight: 1.5 },
  confirmActions: { px: 3, pb: 3, justifyContent: "center", gap: 1 },
  revDetailName: { fontWeight: 600, fontSize: 16 },
  revContact: { fontSize: 12, color: "#776B5D" },
  persBarTitle: { fontWeight: 600, fontSize: 12.5, mb: 0.5 },
  persBarMeta: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 1.5,
    mb: 0.5,
    fontSize: 12.5,
    fontWeight: 700,
    color: PURPLE_700,
    "& i": { fontSize: 12, mr: 0.5 },
  },
  persBarMetaItem: {
    display: "inline-flex",
    alignItems: "center",
    minWidth: 0,
  },
  persBarText: { fontSize: 12.5, color: "#776B5D" },
  emailSubjectVal: { flex: 1, fontSize: 14, color: "#151e16", fontWeight: 600 },
  regenErrorIcon: { color: "#C0392B", fontSize: 18 },
  caveatSwatch: {
    backgroundColor: "rgba(177,179,248,0.30)",
    borderRadius: "4px",
    width: 12,
    height: 12,
    mt: "2px",
  },
  rmTitle: { fontWeight: 700, fontSize: 21 },
  rmTitleIcon: { color: PURPLE_500, mr: 1 },
  rmSub: { fontSize: 13.5, color: "#776B5D", mt: 0.75 },
  rmGroupLabel: { fontWeight: 600, fontSize: 15, mt: 2, mb: 1 },
  rmCredits: { fontSize: 13, color: "#776B5D", mr: "auto" },
  rmCheck: (on: boolean) => ({
    display: "flex",
    alignItems: "center",
    gap: 1.5,
    border: `1px solid ${on ? PRIMARY : BORDER}`,
    borderRadius: "12px",
    p: "13px 15px",
    mb: 1,
    cursor: "pointer",
    fontSize: 14,
  }),
  rmCheckBox: (on: boolean) => ({
    width: 20,
    height: 20,
    borderRadius: "6px",
    flexShrink: 0,
    border: `2px solid ${on ? PRIMARY : BORDER}`,
    backgroundColor: on ? PRIMARY : "transparent",
    color: "#fff",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
  }),
};

export default styles;
